import React from 'react';
import Divider from 'components/divider/Divider';
import { ArrowRightIcon, CrossIconvector, SearchWithBgIcon } from 'assets/icons';
import { CustomButton, CustomTextArea, NoPermissions } from 'components';
import { ManualPaymentMethods } from 'containers/home/components/constants';
import { formatAmount } from 'utils/amountUtils';
import { InvoiceByIdResponse } from '../invoices.model';

interface MarkPaidMainSectionProps {
  isRootState: boolean;
  handleSidePanelBack: () => void;
  invoiceData: InvoiceByIdResponse;
  hasManualPaymentMethod: boolean;
  selectedMethodId: string;
  referenceNote: string;
  handleReferenceNoteChange: (value: string) => void;
  setSelectedMethodId: (value: string) => void;
  markPaidHandler: () => void;
  markPaidLoading: boolean;
}

const MarkPaidMainSection: React.FC<MarkPaidMainSectionProps> = ({
  isRootState,
  handleSidePanelBack,
  invoiceData,
  hasManualPaymentMethod,
  selectedMethodId,
  referenceNote,
  handleReferenceNoteChange,
  setSelectedMethodId,
  markPaidHandler,
  markPaidLoading
}) => {
  const showReferenceNotes = (methodKey: string) => {
    return methodKey === selectedMethodId;
  };

  const handleMethodChange = (methodKey: string) => {
    setSelectedMethodId(methodKey);
    handleReferenceNoteChange('');
  };

  return (
    <>
      <div className="flex flex-row items-center p-4">
        <div
          id={`${isRootState ? 'mark-as-paid-panel-close-button' : 'mark-as-paid-panel-back-button'}`}
          onClick={handleSidePanelBack}
          className="cursor-pointer">
          {isRootState ? <CrossIconvector className="shrink-0" /> : <ArrowRightIcon className="rotate-180" />}
        </div>
        <div className="pl-2 text-17px font-bold text-primaryText">{'Mark as Paid'}</div>
      </div>
      <Divider variant="fullWidth" className="border-secondaryBtn" />
      {invoiceData.isModifiable && hasManualPaymentMethod ? (
        <div className={'mt-8 px-4'}>
          <div className="flex flex-col items-center">
            <div id="mark-as-paid-panel-header" className={'text-17px font-semibold text-primaryText'}>
              Mark as fully paid
            </div>
            <div className={'mt-8 text-px13 font-normal text-primaryText'}>Amount</div>
            <div id="mark-as-paid-panel-amount" className={'mt-2 text-3xl font-bold text-tertiaryText'}>
              ${formatAmount(invoiceData?.amount)}
            </div>
          </div>

          <div className="mt-8">
            {ManualPaymentMethods.map(method => {
              return (
                <div key={method.key}>
                  <div className="mb-4 flex items-center ">
                    <input
                      id={`default-checkbox-${method.key}`}
                      type="radio"
                      value=""
                      onChange={() => handleMethodChange(method.key)}
                      checked={selectedMethodId == method.key}
                      className="h-4 w-4 cursor-pointer rounded pl-1 accent-primaryBtn"
                    />
                    <label
                      htmlFor={`default-checkbox-${method.key}`}
                      className="ml-5 text-sbase font-normal text-primaryText">
                      {method.value}
                    </label>
                  </div>
                  {showReferenceNotes(method.key) && (
                    <div className="mt-2 mb-4">
                      <div className="mb-2 text-sbase font-semibold text-primaryText">Reference or notes</div>
                      <CustomTextArea
                        resizeTextArea={true}
                        id="zelle-payment-notes"
                        maxLength={1000}
                        name="zelleNotes"
                        placeholder="Enter amount / date received or any other relevant information"
                        value={referenceNote}
                        onChange={handleReferenceNoteChange}
                        className="min-h-[70px] py-[15px] text-[15px] font-normal
                          leading-5 text-primaryText outline-none placeholder:text-[15px]"
                        showRemainingChar={true}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <CustomButton
            id="mark-as-paid-confirm-button"
            className="path-stroke-current mt-4 min-h-[42px]
              w-full bg-primaryBtn py-2 font-lato normal-case text-primaryBtnText disabled:opacity-25"
            onClick={markPaidHandler}
            disabled={!invoiceData.isModifiable}
            isLoading={markPaidLoading}>
            <div className="font-semibold">Confirm</div>
          </CustomButton>

          <button
            id="mark-as-paid-cancel-button"
            className="mt-4 min-h-[42px] w-full rounded-md
              border-primaryBtn bg-primaryBtnText py-2 font-lato text-primaryBtn shadow-sm hover:bg-[#ECF1F7]"
            onClick={handleSidePanelBack}
            style={{ borderWidth: 1 }}>
            <div className="font-semibold">Cancel</div>
          </button>
        </div>
      ) : (
        <>
          {!invoiceData.isModifiable && <NoPermissions />}
          {!hasManualPaymentMethod && (
            <div className="mt-10 flex flex-col items-center gap-2 text-sbase font-semibold text-primaryText">
              <div>
                <SearchWithBgIcon />
              </div>
              <div>No manual payment methods found</div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default MarkPaidMainSection;
